import type { AnalysisJob, AnalysisSlide, AnalysisSummary, DepositAnalysis, DepositBackend } from "@opulancia/deposit-core";
import { formatDate, h, notice, plural, replace } from "../dom.js";
import { ficheSection } from "./fiche.js";

const BAND: Record<string, string> = { top: "haut", middle: "milieu", bottom: "bas" };
const VERDICTS: Array<[keyof Pick<DepositAnalysis["summary"]["reference_comparison"]["items"][number], "consistent" | "uncertain" | "inconsistent" | "not_measured">, string]> = [
  ["consistent", "cohérent"],
  ["uncertain", "incertain"],
  ["inconsistent", "contraire"],
  ["not_measured", "non mesuré"],
];

const swatch = (hex: string, label?: string) =>
  h("span", { class: "swatch", title: label ? `${hex} · ${label}` : hex }, h("span", { class: "swatch-dot", style: `background:${hex}` }), hex);

/**
 * The stored analysis of a deposit, read only. What was read and measured comes first; the
 * comparison with the ZOO reference is folded and presented as one reference, not a verdict
 * on the carousel.
 */
export function analysisSection(backend: DepositBackend, depositId: string, depositNumber?: number): HTMLElement {
  const root = h("section", { class: "panel analysis", "aria-label": "Analyse", "data-testid": "analysis" }, h("h2", null, "Analyse"), h("p", { class: "muted" }, "Chargement…"));
  if (!backend.getAnalysis) {
    replace(root, h("h2", null, "Analyse"), h("p", { class: "muted" }, "Indisponible ici."));
    return root;
  }
  Promise.all([backend.getAnalysis(depositId), backend.getAnalysisJob ? backend.getAnalysisJob(depositId) : Promise.resolve(null)])
    .then(([analysis, job]) => replace(root, h("h2", null, "Analyse"), ...state(analysis, job, depositNumber), analysis ? ficheSection(backend, depositId) : null))
    .catch((error) => replace(root, h("h2", null, "Analyse"), h("p", { class: "muted", style: "margin:0" }, `Analyse indisponible : ${error instanceof Error ? error.message : String(error)}`)));
  return root;
}

const NO_DELAY_PROMISE = "L'analyse suit la vérification d'intégrité quotidienne ; un échec ou une file d'attente peut la retarder.";

/** What the members see: the stored analysis, and above it the latest job when it says more
 * (running, or failed with its error, its attempts and what it measured). */
function state(analysis: DepositAnalysis | null, job: AnalysisJob | null, depositNumber?: number): Array<HTMLElement | null> {
  const jobNote = job && job.current && job.status !== "done" ? jobNotice(job, depositNumber) : null;
  if (analysis) return [jobNote, ...render(analysis.summary, { version: analysis.analyzer_version, at: analysis.created_at, current: analysis.current })];
  if (jobNote && job?.status === "failed" && job.partial_summary) {
    return [jobNote, h("p", { class: "chip warn" }, "Résultat partiel, non conservé comme analyse complète"), ...render(job.partial_summary, { version: job.analyzer_version, at: job.updated_at, current: true }, true)];
  }
  if (jobNote) return [jobNote];
  return [h("p", { class: "muted", style: "margin:0" }, `Pas encore analysé. ${NO_DELAY_PROMISE}`)];
}

function jobNotice(job: AnalysisJob, depositNumber?: number): HTMLElement {
  if (job.status === "running") return notice("info", "Analyse en cours", `Essai ${job.attempts} (version ${job.analyzer_version}).`);
  const exhausted = job.attempts >= 3;
  return notice(
    exhausted ? "bad" : "warn",
    exhausted ? `Analyse abandonnée après ${job.attempts} essais` : `Analyse en échec (essai ${job.attempts} sur 3)`,
    h(
      "div",
      null,
      h("p", { style: "margin:0 0 6px;overflow-wrap:anywhere" }, job.last_error ?? "Erreur inconnue."),
      h(
        "p",
        { class: "muted", style: "margin:0;font-size:13px" },
        exhausted
          ? `Relance manuelle : GitHub › Actions › deposit-analysis › Run workflow, mode « pending », champ « retry » = ${depositNumber ?? "numéro du dépôt"}.`
          : "Nouvel essai automatique au prochain passage de l'analyse (après la vérification quotidienne).",
      ),
    ),
  );
}

const TEXT_STATE: Record<string, string> = {
  failed: "lecture du texte en échec",
  not_run: "texte non lu (OCR non exécuté)",
};

function render(s: AnalysisSummary, meta: { version: string; at: string; current: boolean }, partial = false): Array<HTMLElement | null> {
  const analysed = s.slides.filter((x) => x.status === "analyzed");
  const withText = analysed.filter((x) => (x.editorial ?? []).length);
  const inPhoto = analysed.filter((x) => (x.in_photo ?? []).length);
  const brandSlides = analysed.filter((x) => (x.brand ?? []).length);
  const brandTexts = [...new Set(brandSlides.flatMap((x) => x.brand ?? []))];
  const doubtful = analysed.reduce((n, x) => n + (x.doubtful ?? 0), 0);
  const failed = s.slides.filter((x) => x.status === "failed");
  const unread = analysed.filter((x) => x.text_status && x.text_status !== "read");
  const emptyRead = analysed.filter((x) => x.text_status === "read" && x.lines_read === 0);
  const split = analysed.filter((x) => x.composition?.status === "split_detected");
  const measuredComposition = analysed.some((x) => x.composition);
  const ref = s.reference_comparison;
  return [
    h(
      "p",
      { class: "muted", style: "margin:0 0 12px;font-size:13px" },
      `Version ${meta.version} · ${formatDate(meta.at)} · ${plural(s.slides_analyzed, "slide mesurée", "slides mesurées")}`,
      meta.current ? null : h("b", { style: "color:var(--warn)" }, " · l'ordre ou les slides ont changé depuis : une nouvelle analyse suivra"),
    ),
    failed.length || unread.length
      ? h(
          "ul",
          { class: "analysis-errors", "data-testid": "analysis-errors" },
          failed.map((x) => h("li", null, h("b", null, `Slide ${x.position} non analysée : `), x.error ?? "erreur inconnue")),
          unread.map((x) => h("li", null, h("b", null, `Slide ${x.position} : ${TEXT_STATE[x.text_status!] ?? x.text_status}`), x.text_error ? ` — ${x.text_error}` : "", " (mesures visuelles conservées)")),
        )
      : null,

    h("h3", null, "Texte éditorial"),
    withText.length
      ? h(
          "ol",
          { class: "analysis-text" },
          withText.map((x) =>
            h(
              "li",
              { value: String(x.position ?? "") },
              (x.editorial ?? []).map((line, i) =>
                h(
                  "span",
                  { class: `line${line.on_light_block ? " on-block" : ""}`, title: `${BAND[line.band] ?? line.band}${line.on_light_block ? " · sur un bloc" : ""}${line.uncertain ? " · classement incertain" : ""}` },
                  line.text,
                  line.uncertain ? h("span", { class: "muted" }, " (?)") : null,
                  i < (x.editorial ?? []).length - 1 ? " / " : null,
                ),
              ),
            ),
          ),
        )
      : h("p", { class: "muted" }, partial ? "Aucun texte éditorial lu sur les slides lues." : "Aucun texte éditorial lu."),
    brandSlides.length
      ? h("p", { class: "muted", style: "font-size:13px", "data-testid": "analysis-brand" }, `Marque, à part du texte éditorial : ${brandTexts.slice(0, 4).join(" · ")} — ${plural(brandSlides.length, "slide", "slides")} sur ${analysed.length}.`)
      : s.lines_by_kind.brand === undefined && withText.some((x) => (x.editorial ?? []).some((l) => /PULANC/i.test(l.text)))
        ? h("p", { class: "muted", style: "font-size:13px" }, "Version antérieure : le logo OPULANCIA est compté dans le texte éditorial ci-dessous ; la version 0.3.0 le met à part.")
        : null,
    emptyRead.length
      ? h("p", { class: "muted", style: "font-size:13px" }, `Aucune ligne lue sur ${plural(emptyRead.length, "slide", "slides")} (${emptyRead.map((x) => x.position).join(", ")}) : slide sans texte, ou texte que la lecture n'a pas vu — cela ne prouve pas l'absence de texte.`)
      : null,

    inPhoto.length
      ? h("details", { class: "note" }, h("summary", null, `Inscriptions dans les photos (${inPhoto.reduce((n, x) => n + (x.in_photo ?? []).length, 0)}) — enseignes, logos de la scène`), h("ul", null, inPhoto.map((x) => h("li", null, `Slide ${x.position} : ${(x.in_photo ?? []).join(" · ")}`))))
      : null,
    doubtful ? h("p", { class: "muted", style: "font-size:13px" }, `${plural(doubtful, "détection douteuse écartée", "détections douteuses écartées")} (confiance basse, écriture inattendue ou signes isolés).`) : null,

    measuredComposition
      ? h(
          "p",
          { class: "muted", style: "font-size:13px" },
          split.length
            ? `Composition en deux images détectée : ${split.map((x) => `slide ${x.position} (${x.composition!.axis === "vertical" ? "côte à côte" : "l'une au-dessus de l'autre"})`).join(", ")}. `
            : "Aucune composition en deux images détectée. ",
          "Une séparation fondue ou peu contrastée peut échapper à la mesure : une absence de détection reste incertaine.",
        )
      : h("p", { class: "muted", style: "font-size:13px" }, "Composition en deux images : non mesurée par cette version."),

    h("h3", null, "Couleurs"),
    h("div", { class: "swatch-row" }, h("span", { class: "swatch-label" }, "Graphiques (texte et blocs ajoutés)"), s.graphic_colors.length ? s.graphic_colors.map((c) => swatch(c.hex, `${c.count} ligne(s)`)) : h("span", { class: "muted" }, "aucune")),
    h("div", { class: "swatch-row" }, h("span", { class: "swatch-label" }, "Photos (texte exclu)"), s.photo_colors.map((c) => swatch(c.hex, `${Math.round(c.weight * 100)} %`))),

    h(
      "details",
      { class: "note" },
      h("summary", null, `Comparaison avec la référence ZOO (référence particulière, non imposée)`),
      h("p", { class: "muted", style: "font-size:13px;margin:4px 0 8px" }, ref.note),
      h(
        "table",
        { class: "verdicts" },
        h("thead", null, h("tr", null, h("th", null, "Élément"), VERDICTS.map(([, label]) => h("th", null, label)))),
        h("tbody", null, ref.items.map((item) => h("tr", null, h("td", null, item.statement), VERDICTS.map(([key]) => h("td", null, String(item[key])))))),
      ),
    ),
    h("details", { class: "note" }, h("summary", null, "Limites de l'analyse"), h("ul", null, s.limits.map((l) => h("li", null, l)))),
  ];
}

/** A FICTITIOUS analysis for the demo (nothing is analysed in the browser). */
export function demoAnalysis(slides: Array<{ position: number | null }>, createdAt: string): DepositAnalysis {
  const items: AnalysisSlide[] = slides.map((s, i) => ({
    position: s.position,
    status: "analyzed",
    text_status: "read",
    lines_read: i === 0 ? 3 : i === 1 || i === 2 ? 2 : 1,
    composition: { status: "not_detected", axis: null, position: null },
    brand: ["OPULANCIA"],
    editorial: i === 0 ? [{ text: "TITRE FICTIF EN CAPITALES", band: "bottom", on_light_block: false, uncertain: false }, { text: "MOT EN OCRE", band: "bottom", on_light_block: false, uncertain: false }] : [],
    in_photo: i === 1 ? ["ENSEIGNE (FICTIF)"] : [],
    doubtful: i === 2 ? 1 : 0,
    graphic_colors: i === 0 ? ["#FFFFFF", "#DDA84D"] : [],
    photo_colors: ["#E3DAD1", "#261C12"],
    size: [1080, 1350],
  }));
  return {
    analyzer_version: "démo",
    created_at: createdAt,
    current: true,
    summary: {
      analyzer_version: "démo",
      generated_at: createdAt,
      complete: true,
      slides_analyzed: slides.length,
      slides_failed: 0,
      text_read: slides.length,
      text_failed: 0,
      text_not_run: 0,
      text_read_empty: 0,
      lines_by_kind: { editorial: 2, brand: slides.length, in_photo: 1, doubtful: 1 },
      photo_colors: [{ hex: "#E3DAD1", weight: 0.4, count: slides.length }, { hex: "#261C12", weight: 0.2, count: slides.length }],
      graphic_colors: [{ hex: "#FFFFFF", weight: 1, count: 1 }, { hex: "#DDA84D", weight: 1, count: 1 }],
      light_block_lines: 0,
      slides: items,
      reference_comparison: {
        reference: "opulancia_zoo_signature_v1",
        scope: "Habillage d'une publication",
        status: "visual_reference_locked_font_unidentified",
        note: "DÉMO FICTIVE — référence particulière (habillage d'une publication), comparée sans être imposée.",
        items: [{ id: "zoo.headline_bottom", statement: "Titre en capitales, blanc, en bas du cadre", consistent: 1, inconsistent: slides.length - 1, uncertain: 0, not_measured: 0, cover: "consistent" }],
      },
      limits: ["DÉMO FICTIVE : aucune image n'est analysée dans le navigateur."],
    },
  };
}
